import React from "react";

import { getAllocationPercentages } from "../model/calculations";
import { formatKpi } from "../model/format";
import { getTargetFillPercentages } from "../model/targets";
import { AllocationLabels, AllocationSplit, KpiKey } from "../model/types";

interface AllocationMixProps {
  split: AllocationSplit;
  metric: KpiKey;
  labels: AllocationLabels;
  /** When set, the bar fills to its share of this instead of always reading as 100%. */
  target?: number | null;
}

const percent = (value: number) => `${Math.round(value * 100)}%`;

export default function AllocationMix(props: AllocationMixProps) {
  const { split, metric, labels, target = null } = props;
  // A target turns the bar into "progress toward the target" (it can read under 100%);
  // without one it is "how this total splits between its two sources" (always 100%).
  const percentages =
    getTargetFillPercentages(split, target) ?? getAllocationPercentages(split);
  const displayValues = split.displayValues ?? split;
  const formatValue = (value: number) => formatKpi(metric, value);
  const title = [
    `${labels.reportingDate}: ${percent(percentages.reportingDate)} (${formatValue(
      displayValues.reportingDate,
    )})`,
    `${labels.budgetMonth}: ${percent(percentages.budgetMonth)} (${formatValue(
      displayValues.budgetMonth,
    )})`,
    ...(target !== null ? [`Target: ${formatValue(target)}`] : []),
  ].join("; ");

  return (
    <div className="finance-reporting-allocation" title={title}>
      <div
        className="finance-reporting-allocation__bar"
        aria-label={title}
        role="img"
      >
        <i
          className="finance-reporting-allocation__ad-date"
          style={{ width: percent(percentages.reportingDate) }}
        />
        <i
          className="finance-reporting-allocation__budget-month"
          style={{ width: percent(percentages.budgetMonth) }}
        />
      </div>
      <div className="finance-reporting-allocation__values">
        <div className="finance-reporting-allocation__value">
          <span>{labels.reportingDate}</span>
          <strong>{formatValue(displayValues.reportingDate)}</strong>
        </div>
        <div className="finance-reporting-allocation__value finance-reporting-allocation__value--forecast">
          <span>{labels.budgetMonth}</span>
          <strong>{formatValue(displayValues.budgetMonth)}</strong>
        </div>
      </div>
      {target !== null && (
        <div className="finance-reporting-allocation__target">
          Target <strong>{formatValue(target)}</strong>
        </div>
      )}
    </div>
  );
}
